import { memo, type ReactNode, useContext } from 'react';

import { MCQ_ANSWER_STYLES } from '@didaskill/domain/assignments/constants';
import {
  CheckboxControl,
  type CheckboxControlProps,
  CheckboxGroupContext,
} from '@didaskill/ui/design-system';
import { useFocus } from '@didaskill/ui/utils';

import { McqAnswer, type McqAnswerProps } from '../McqAnswer';

export type McqAnswerCheckboxProps = Omit<
  CheckboxControlProps,
  'onChange' | 'checked' | 'value' | 'media'
> & {
  value: string;
  label: McqAnswerProps['label'];
  answerStyle?: McqAnswerProps['answerStyle'];
  media?: McqAnswerProps['media'];
  isCorrected?: boolean;
  isCorrectAnswer?: boolean;
  successText?: ReactNode;
  errorText?: ReactNode;
};

export const McqAnswerCheckboxBase = ({
  label,
  value,
  answerStyle = MCQ_ANSWER_STYLES.TEXT,
  media = null,
  isCorrected = false,
  isCorrectAnswer = false,
  disabled = false,
  successText = null,
  errorText = null,
  ...props
}: McqAnswerCheckboxProps) => {
  const { isFocused, ...focusProps } = useFocus();

  const { value: selectedValue, onChange } = useContext(CheckboxGroupContext);

  const isChecked = !!selectedValue?.includes(value);
  const isDisabled = disabled || isCorrected;

  const isSuccess = !!(isCorrected && isCorrectAnswer && isChecked);

  const isError = !!(
    isCorrected &&
    ((!isCorrectAnswer && isChecked) || (isCorrectAnswer && !isChecked))
  );

  const feedbackText = isCorrected && isCorrectAnswer ? successText : errorText;

  return (
    <McqAnswer
      label={label}
      answerStyle={answerStyle}
      media={media}
      isSelected={isChecked}
      isFocused={isFocused}
      isCorrected={isCorrected}
      isCorrectAnswer={isCorrectAnswer}
      isDisabled={isDisabled}
      isSuccess={isSuccess}
      isError={isError}
      feedbackText={feedbackText}
    >
      <CheckboxControl
        {...props}
        {...focusProps}
        value={value}
        onChange={onChange}
        disabled={isDisabled}
        checked={isChecked}
      />
    </McqAnswer>
  );
};

McqAnswerCheckboxBase.displayName = 'McqAnswerCheckbox';

export const McqAnswerCheckbox = memo(McqAnswerCheckboxBase);
